import type { SubscriptionFrequency } from "@brandclub/common-ui";
import { create } from "zustand";

export interface AddToBagIntentPayload {
  stacklineSku: string;
  retailerId?: number;
  quantity?: number;
  /** Absent: a one-time line. */
  subscriptionFrequency?: SubscriptionFrequency;
}

/**
 * What one add answered a host.
 * - `added`: the cart holds the item, and the bag drawer is open.
 * - `retailerPage`: the product cannot be checked out here, so the retailer's product page opened.
 * - `refused`: nothing was added. `message` is a sentence for the shopper.
 */
export type AddToBagOutcome =
  | { status: "added" }
  | { status: "retailerPage" }
  | { status: "refused"; message: string };

/** The sentence for an add that failed for a reason the shopper cannot act on. */
export const ADD_NOT_FINISHED = "We could not add this item to your bag. Please try again.";

/** One add the controller has not started. `settle` answers the host that asked for it. */
export interface AddToBagJob extends AddToBagIntentPayload {
  settle: (outcome: AddToBagOutcome) => void;
}

interface AddToBagIntentState {
  /** The adds asked for and not yet started, in order. */
  queue: AddToBagJob[];
  /** Goes up with each request, so the controller runs again. */
  requestId: number;
  request: (job: AddToBagJob) => void;
  /** Remove and give the adds not yet started. Each job is taken once. */
  take: () => AddToBagJob[];
}

export const useAddToBagIntent = create<AddToBagIntentState>((set, get) => ({
  queue: [],
  requestId: 0,
  request: (job) =>
    set((state) => ({
      queue: [...state.queue, job],
      requestId: state.requestId + 1,
    })),
  take: () => {
    const { queue } = get();
    if (queue.length) set({ queue: [] });
    return queue;
  },
}));
